//src/hooks/chatHook.ts
import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "./storeHook";
import { ChatAction } from "../actions";
import { ActionTypes } from "../store/actionTypes";
import { useSSE } from "./useSSE";
import { getSSE } from "../shared/utils/sseSingleton";
import type { AssistantMode, ChatAttachment, HandleMessageAutomationOptions } from "../apis/chatApi";
import { dispatchChatSendFailure } from "../utils/chatSendFailure";
import {
  canSendQueuedUserMessageNow,
  canSendQueuedUserMessage,
  hasStreamingAssistantMessage,
  shouldQueueNewUserMessage,
} from "../shared/utils/chatQueueUtils";

export interface SendMessageOptions {
  interruptAutoRun?: boolean;
  structuredWorkflow?: Record<string, unknown>;
  automation?: HandleMessageAutomationOptions;
  attachments?: ChatAttachment[];
  assistantMode?: AssistantMode;
}

export interface UseChatHookOptions {
  aiThreadIdOverride?: string | null;
  skipInitialize?: boolean;
  /** Called when Cortex persists a graph-generated title for the active thread. */
  onThreadName?: (threadName: string) => void;
}

type QueuedUserMessage = {
  id: string;
  content: string;
  options?: SendMessageOptions;
};

export function useChatHook(ticketId: string | null, userId: string, options?: UseChatHookOptions) {
  const dispatch = useAppDispatch();
  const { messages, aiThreadId, ticketId: storedTicketId, isLoading, error, isThinking } = useAppSelector(
    (state) => state.chat
  );
  // Navigation renders before the new ticket thread resolves. Do not use the previous one.
  const belongsToTicket = !ticketId || String(storedTicketId ?? "") === String(ticketId);
  const [validatedTicketKey, setValidatedTicketKey] = useState<string | null>(null);
  const awaitingTicketValidation = Boolean(ticketId && !options?.skipInitialize && validatedTicketKey !== ticketId);
  const effectiveAiThreadId = options?.aiThreadIdOverride ||
    (belongsToTicket && !awaitingTicketValidation ? aiThreadId : null);

  const localThreadIdRef = useRef<string | null>(null);
  const lastTicketOrUserIdRef = useRef<string | null>(null);
  const queuedMessagesRef = useRef<QueuedUserMessage[]>([]);
  const [queuedMessages, setQueuedMessages] = useState<QueuedUserMessage[]>([]);
  const sendInFlightRef = useRef(false);
  const stopRequestedRef = useRef(false);
  const queueThreadIdRef = useRef<string | null>(null);
  
  const syncQueuedMessages = useCallback((
    updater: QueuedUserMessage[] | ((current: QueuedUserMessage[]) => QueuedUserMessage[])
  ) => {
    const next =
      typeof updater === "function"
        ? (updater as (current: QueuedUserMessage[]) => QueuedUserMessage[])(queuedMessagesRef.current)
        : updater;
    queuedMessagesRef.current = next;
    setQueuedMessages(next);
  }, []);

  useEffect(() => {
    const currentKey = ticketId || userId;

    if (currentKey !== lastTicketOrUserIdRef.current) {
      localThreadIdRef.current = null;
      lastTicketOrUserIdRef.current = currentKey;
    }

    if (currentKey === lastTicketOrUserIdRef.current) {
      if (effectiveAiThreadId && effectiveAiThreadId !== localThreadIdRef.current) {
        localThreadIdRef.current = effectiveAiThreadId;
      } else if (!effectiveAiThreadId && localThreadIdRef.current) {
        localThreadIdRef.current = null;
      }
    }
  }, [effectiveAiThreadId, ticketId, userId]);

  useEffect(() => {
    const threadKey = effectiveAiThreadId ? String(effectiveAiThreadId) : null;
    if (queueThreadIdRef.current === threadKey) return;
    syncQueuedMessages([]);
    sendInFlightRef.current = false;
    stopRequestedRef.current = false;
    queueThreadIdRef.current = threadKey;
  }, [effectiveAiThreadId, syncQueuedMessages]);

  // Prefer the current effective thread id so device/ticket overrides win over a stale ref.
  const sseThreadId = effectiveAiThreadId;

  const { isConnected, connectionStatus, error: sseError } = useSSE(sseThreadId, userId, {
    ticketId: ticketId ?? undefined,
    onThreadName: options?.onThreadName,
  });

  const initializedRef = useRef<string | null>(null);
  const initializingRef = useRef<string | null>(null);

  useEffect(() => {
    const initializeChat = async () => {
      if (options?.skipInitialize) return;
      const key = ticketId || userId;
      if (!key || initializingRef.current === key) return;

      if (!effectiveAiThreadId && initializedRef.current === key) {
        initializedRef.current = null;
      }

      const needsInitialization = !effectiveAiThreadId || initializedRef.current !== key;
      
      if (!needsInitialization) {
        return;
      }
      
      initializedRef.current = key;
      initializingRef.current = key;
      
      try {
        if (ticketId) {
          const response = await dispatch(ChatAction.getTicketThread(ticketId));
          if (!response?.ai_thread) {
            await dispatch(ChatAction.createTicketThread(ticketId));
          }
        } else if (userId) {
          const response = await dispatch(ChatAction.getUserThread(userId));
          if (!response?.ai_thread) {
            await dispatch(ChatAction.createUserThread(userId));
          }
        }
        if (initializingRef.current === key && ticketId) setValidatedTicketKey(key);
      } catch (error) {
        dispatch({ type: ActionTypes.CHAT_ERROR, payload: error });
        if (initializedRef.current === key) {
          initializedRef.current = null;
        }
      } finally {
        if (initializingRef.current === key) initializingRef.current = null;
      }
    };

    initializeChat();
  }, [ticketId, userId, effectiveAiThreadId, dispatch, options?.skipInitialize]);

  useEffect(() => {
    const key = ticketId || userId;
    if (key && initializedRef.current !== key) {
      initializedRef.current = null;
      }
  }, [ticketId, userId]);

  const sendMessageNow = useCallback(async (content: string, options?: SendMessageOptions) => {
    const threadIdToUse = effectiveAiThreadId;
    if (!threadIdToUse) {
      return;
    }

    sendInFlightRef.current = true;
    stopRequestedRef.current = false;

    if (localThreadIdRef.current !== threadIdToUse) {
      localThreadIdRef.current = threadIdToUse;
    }
    getSSE(threadIdToUse)?.connect();
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    dispatch({
      type: ActionTypes.ADD_MESSAGE,
      payload: {
        message_id: `temp_${requestId}`,
        ai_thread_id: threadIdToUse,
        user_id: userId,
        content:
          options?.attachments?.length
            ? `${content}${content.trim() ? "\n\n" : ""}${options.attachments
                .map((item) => `[Attachment: ${item.filename}]`)
                .join("\n")}`
            : content,
        content_type: "MESSAGE",
        created_at: { seconds: `${Math.floor(Date.now() / 1000)}`, nanos: 0 },
        pending: true,
        requestId,
        automation_capacity: options?.automation?.automation_capacity,
        // The picked agent, so the message's Auto Resolver badge can name it before the server does.
        agent_id: options?.automation?.agent_id,
      },
    });

    dispatch({ type: ActionTypes.CHAT_LOADING, payload: true });
    dispatch({ type: ActionTypes.START_THINKING });

    try {
      await dispatch(
        ChatAction.handleMessage(
          threadIdToUse,
          userId,
          content,
          options?.structuredWorkflow,
          requestId,
          {
            automation: options?.automation,
            assistantMode: options?.assistantMode,
            attachments: options?.attachments,
          }
        )
      );
    } catch (error: any) {
      console.error("[chatHook] Failed to send message:", error);
      dispatchChatSendFailure(dispatch, error, requestId);
    } finally {
      sendInFlightRef.current = false;
    }
  }, [dispatch, effectiveAiThreadId, userId]);

  const drainQueuedMessage = useCallback(() => {
    if (sendInFlightRef.current) return;
    if (queuedMessagesRef.current.length === 0) return;
    if (
      !canSendQueuedUserMessage(messages, {
        isLoading,
        isThinking,
        allowAfterLastUserMessage: stopRequestedRef.current,
      })
    ) {
      return;
    }

    const next = queuedMessagesRef.current[0];
    if (!next) return;
    syncQueuedMessages(queuedMessagesRef.current.slice(1));
    void sendMessageNow(next.content, next.options);
  }, [isLoading, isThinking, messages, sendMessageNow, syncQueuedMessages]);

  useEffect(() => {
    drainQueuedMessage();
  }, [drainQueuedMessage]);

  const sendMessage = async (content: string, options?: SendMessageOptions) => {
    if (!effectiveAiThreadId) {
      return;
    }

    if (options?.interruptAutoRun) {
      syncQueuedMessages((current) => current.filter((item) => item.options?.automation?.automation_capacity === 0));
      await sendMessageNow(content, options);
      return;
    }

    if (
      sendInFlightRef.current ||
      shouldQueueNewUserMessage(messages, {
        isLoading,
        isThinking,
      })
    ) {
      syncQueuedMessages((current) => [
        ...current,
        {
          id: `queued_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
          content,
          options,
        },
      ]);
      return;
    }

    await sendMessageNow(content, options);
  };

  const deleteQueuedMessage = useCallback((id: string) => {
    syncQueuedMessages((current) => current.filter((item) => item.id !== id));
  }, [syncQueuedMessages]);

  const requestStopTurn = useCallback(async () => {
    const threadIdToUse = effectiveAiThreadId || localThreadIdRef.current;
    if (!threadIdToUse) return;
    stopRequestedRef.current = true;
    try {
      await dispatch(ChatAction.cancelTurn(threadIdToUse));
    } catch (error) {
      console.error("[chatHook] Failed to stop turn:", error);
    }
  }, [dispatch, effectiveAiThreadId]);

  const canSendQueuedMessagesNow = canSendQueuedUserMessageNow(messages, {
    isLoading,
    isThinking,
    canCancelActiveTurn: Boolean(effectiveAiThreadId || localThreadIdRef.current),
  });

  const sendQueuedMessageNow = useCallback((id: string) => {
    const threadIdToUse = effectiveAiThreadId || localThreadIdRef.current;
    if (!threadIdToUse) return;
    const next = queuedMessagesRef.current.find((item) => item.id === id);
    if (!next) return;

    const activeTurn =
      sendInFlightRef.current ||
      isLoading ||
      isThinking ||
      hasStreamingAssistantMessage(messages);

    if (activeTurn) {
      syncQueuedMessages((current) => [
        next,
        ...current.filter((item) => item.id !== id),
      ]);
      void requestStopTurn();
      return;
    }

    if (
      !canSendQueuedUserMessageNow(messages, {
        isLoading,
        isThinking,
      })
    ) {
      return;
    }
    syncQueuedMessages((current) => current.filter((item) => item.id !== id));
    void sendMessageNow(next.content, next.options);
  }, [effectiveAiThreadId, isLoading, isThinking, messages, requestStopTurn, sendMessageNow, syncQueuedMessages]);

  const updateQueuedMessage = useCallback((id: string, content: string) => {
    const trimmed = String(content || "").trim();
    syncQueuedMessages((current) =>
      current.flatMap((item) => {
        if (item.id !== id) return [item];
        const attachments = item.options?.attachments ?? [];
        const hasAttachments = attachments.length > 0;
        if (!trimmed && !hasAttachments) return [];
        return [{ ...item, content: trimmed }];
      })
    );
  }, [syncQueuedMessages]);

  const stopTurn = requestStopTurn;

  return {
    messages: effectiveAiThreadId ? messages : [],
    aiThreadId: effectiveAiThreadId,
    isLoading,
    error: error || sseError,
    sendMessage,
    stopTurn,
    queuedMessages: queuedMessages.map((item) => ({
      id: item.id,
      content: item.content,
      attachments: item.options?.attachments,
    })),
    deleteQueuedMessage,
    updateQueuedMessage,
    sendQueuedMessageNow,
    canSendQueuedMessagesNow,
    isConnected,
    connectionStatus,
  };
}
